'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { ExternalLink, Loader2, Mail, RotateCw } from 'lucide-react';
import { Button } from '@/app/_module/components/ui/button';
import { useToast } from '@/app/_module/hooks/use-toast';
import { cn } from '@/app/_module/lib/utils';
import { isDemoMode } from '@/app/_module/utils/demoMode';
import { isNativePlatform } from '@/lib/capacitor/platform';
import { allowCheckout } from '@/app/_module/utils/nativeCheckoutGuard';
import A2PWizard from './_components/A2PWizard';
import CallerIdNameCard from './_components/CallerIdNameCard';
import { StatusPanel, LoadingPanel } from './_components/overview/StatusPanel';
import { ReviewSteps, type ReviewStep } from './_components/overview/ReviewSteps';
import { NumbersSection } from './_components/overview/NumbersSection';
import { TextingNumberSection } from './_components/overview/TextingNumberSection';
import { LandingPageSection } from './_components/overview/LandingPageSection';
import { RegistrationGuide } from './_components/overview/RegistrationGuide';
import { SupportIds } from './_components/overview/SupportIds';
import { OtpForm } from './_components/overview/OtpForm';
import { Panel, Section, focusRing, formatDate, type Fact } from './_components/overview/primitives';
import { cardLabel, clearDraft, draftProgress, readDraft, type A2PWizardDraft } from './_components/overview/draft';
import { a2pService, type A2PStatusResponse, type RegistrationFeeState } from '@/app/_module/services/a2pService';
import { useUserContext } from '@/app/_module/store/contexts/userContext';
import { useTrialStatus } from '@/app/_module/hooks/useTrialStatus';
import { useBranding } from '@/app/_module/store/contexts/brandingContext';

const TYPE_LABEL: Record<string, string> = {
  sole_proprietor: 'Sole proprietor',
  low_volume_standard: 'Low Volume Standard',
  standard: 'Standard',
};

export default function A2PVerificationPage() {
  const { toast } = useToast();
  const { user: userProfile } = useUserContext();
  const { branding } = useBranding();

  // An owner/admin can register a campaign for their agency even when they
  // already hold a verified one of their own — the two are different things and
  // coexist (their own always wins for them; the agency one covers everyone
  // else). Without this entry point the wizard is unreachable once verified,
  // and the only "upgrade" affordance is a mailto to support.
  const currentAgencyCtx = (userProfile as any)?.currentAgency;
  const canRegisterForAgency = ['owner', 'admin'].includes(currentAgencyCtx?.role ?? '');

  const [loading, setLoading] = useState(true);
  // A failed load used to fall through to "not registered", inviting a second registration.
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState<A2PStatusResponse | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  // Wizard progress saved in this browser, so an agent who backs out can resume.
  const [draft, setDraft] = useState<A2PWizardDraft | null>(null);
  const [resumeWizard, setResumeWizard] = useState(false);
  // Per-registration fee (white-labels that charge, MRFG): paid while the registration is in our review.
  const [fee, setFee] = useState<RegistrationFeeState | null>(null);
  const [payingFee, setPayingFee] = useState(false);
  const [realtimeChannel, setRealtimeChannel] = useState<any>(null);
  const isUpdatingRef = useRef(false);
  const [eligibility, setEligibility] = useState<{
    canSubmit: boolean;
    blockReason: 'a2p_locked' | 'no_paid_number' | null;
    blockMessage: string;
    locked: boolean;
    lockedAt: string | null;
    lockoutReason: string | null;
    paidNumberCount: number;
    trialNumberCount: number;
  } | null>(null);
  const [eligibilityError, setEligibilityError] = useState(false);
  const [showPhoneNumbers, setShowPhoneNumbers] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<any[]>([]);
  const [selectedActivePhoneId, setSelectedActivePhoneId] = useState<string>("");
  const [swappingActiveNumber, setSwappingActiveNumber] = useState(false);
  // Two-step confirm: switching costs the agent WEEKS of undeliverable texts while the
  // carriers register the new number, and it can't be undone any faster by switching back.
  // They must tick the acknowledgement before the button arms.
  const [swapConfirmOpen, setSwapConfirmOpen] = useState(false);
  const [swapAcknowledged, setSwapAcknowledged] = useState(false);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [relinkingNumbers, setRelinkingNumbers] = useState(false);

  // Native app: A2P registration is web-only (the wizard ends in a Stripe fee
  // + Twilio compliance submission) — replaced with an instruction panel below.
  const [isNativeApp, setIsNativeApp] = useState(false);
  useEffect(() => {
    setIsNativeApp(isNativePlatform());
  }, []);

  // Extract user information
  const user = userProfile?.user;

  // Get trial status - check if user is on trial (not a paid subscriber yet)
  const { isTrialActive, subscriptionStatus } = useTrialStatus();
  const isOnTrial = isTrialActive || subscriptionStatus === 'trialing';

  // Load A2P status on mount
  useEffect(() => {
    loadStatus();
    loadEligibility();
  }, []);

  // Auto-load phone numbers for verified sole prop agents so the active-number swap UI
  // has data to work with without the user having to expand the phone numbers panel first.
  useEffect(() => {
    if (
      status?.status === 'verified' &&
      status?.brandType === 'sole_proprietor' &&
      phoneNumbers.length === 0 &&
      !loadingPhoneNumbers
    ) {
      loadPhoneNumbers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status, status?.brandType]);

  // Real-time subscription to status changes
  useEffect(() => {
    subscribeToA2PUpdates();

    return () => {
      unsubscribeFromA2PUpdates();
    };
  }, [user?.id]);

  // Re-read saved wizard progress whenever the wizard closes.
  useEffect(() => {
    if (!showWizard) setDraft(readDraft(user?.id));
  }, [user?.id, showWizard]);

  // Fee state (white-labels that charge per registration): shown before they
  // register and while the registration is in our review; never once approved.
  useEffect(() => {
    if (!status) return;
    if (status.status === 'verified') setFee(null);
    else loadFee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status]);

  // Back from Stripe Checkout (?a2p_fee=paid|cancelled): say so, then drop the flag from the URL.
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const result = query.get('a2p_fee');
    if (!result) return;
    query.delete('a2p_fee');
    window.history.replaceState(null, '', `${window.location.pathname}${query.toString() ? `?${query}` : ''}`);
    if (result === 'paid') {
      toast({ title: 'Payment received', description: 'Your registration moves on to our review.' });
    } else {
      toast({ title: 'Payment not finished', description: 'Your registration is waiting on the fee. You can pay any time from this tab.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribeToA2PUpdates = async () => {
    if (!user?.id) return;

    try {
      const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
      const supabase = await getSupabaseClient();

      const channel = supabase
        .channel(`a2p-verifications-user-${user.id}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'a2p_verifications',
          filter: `user_id=eq.${user.id}`
        }, (payload: any) => {
          handleRealtimeUpdate(payload.new);
        })
        .subscribe();

      setRealtimeChannel(channel);
    } catch (error) {

    }
  };

  const unsubscribeFromA2PUpdates = async () => {
    if (realtimeChannel) {
      const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
      const supabase = await getSupabaseClient();
      await supabase.removeChannel(realtimeChannel);
    }
  };

  const handleRealtimeUpdate = (updatedVerification: any) => {
    if (isUpdatingRef.current) return;

    isUpdatingRef.current = true;

    setStatus((prevStatus) => ({
      ...prevStatus,
      ...updatedVerification,
      isVerified: updatedVerification.status === 'verified',
      needsVerification: updatedVerification.status !== 'verified',
    }));

    // Show toast notification for status changes
    if (updatedVerification.status === 'verified') {
      toast({
        title: 'Texting registration approved',
        description: 'You can text your leads now.',
      });
    } else if (updatedVerification.status === 'rejected') {
      toast({
        title: 'Registration rejected',
        description: updatedVerification.rejection_reason || 'Open this tab to see what happens next.',
        variant: 'destructive',
      });
    }

    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 100);
  };

  async function loadEligibility() {
    try {
      setEligibilityError(false);
      const { default: api } = await import('@/app/_module/services/api');
      const { getApiUrl } = await import('@/app/_module/services/config');
      const res: any = await api.get({
        // NOTE: no '/v1/' — getApiUrl already prefixes '/api/', and the backend
        // global prefix is just 'api'. The stray '/v1/' made this 404, leaving
        // `eligibility` null → the Start button stayed permanently disabled.
        url: getApiUrl('/communication/a2p/eligibility'),
        auth: true,
      });
      if (res?.data) setEligibility(res.data);
      else setEligibilityError(true);
    } catch {
      // Shown next to the Start button, which stays disabled until this succeeds
      setEligibilityError(true);
    }
  }

  async function loadStatus() {
    try {
      setLoading(true);
      setLoadError(false);
      const statusData = await a2pService.checkStatus();
      setStatus(statusData);
    } catch (error: any) {

      if (error.message?.includes('Authentication required') || error.message?.includes('401')) {

        return;
      }

      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  function openWizard(resume: boolean) {
    if (!resume) {
      clearDraft(user?.id);
      setDraft(null);
    }
    setResumeWizard(resume);
    setShowWizard(true);
  }

  function handleStartVerification() {
    openWizard(!!draft);
  }

  function handleWizardComplete() {
    clearDraft(user?.id);
    setShowWizard(false);
    loadStatus();
  }

  async function loadFee() {
    try {
      setFee(await a2pService.getRegistrationFee());
    } catch {
      setFee(null);
    }
  }

  async function handlePayFee(method?: 'checkout') {
    setPayingFee(true);
    try {
      const result = await a2pService.payRegistrationFee(method);
      if (result.paid) {
        toast({ title: 'Registration fee paid', description: 'Your registration moves on to our review.' });
        await loadFee();
      } else if (result.processing) {
        toast({ title: 'Payment processing', description: "We'll update this once your bank confirms it." });
        await loadFee();
      } else if (result.checkoutUrl) {
        if (result.declined) {
          toast({ title: 'Your card on file was declined', description: 'Finish paying on the secure checkout page.' });
        }
        if (allowCheckout('payment')) {
          window.location.href = result.checkoutUrl;
          return; // leaving for checkout; keep the button busy
        }
      }
    } catch (error: any) {
      toast({ title: "Couldn't take the payment", description: error?.message || 'Try again.', variant: 'destructive' });
    }
    setPayingFee(false);
  }

  // `force` refetches instead of toggling; used after a relink or swap changes the list.
  async function loadPhoneNumbers(force = false) {
    if (!force && phoneNumbers.length > 0) {
      // Already loaded, just toggle
      setShowPhoneNumbers(!showPhoneNumbers);
      return;
    }

    try {
      setLoadingPhoneNumbers(true);
      // Import api and endpoints
      const { default: api } = await import('@/app/_module/services/api');
      const { getApiUrl } = await import('@/app/_module/services/config');
      const { endpoint } = await import('@/app/_module/services/endpoints');

      // Fetch user's phone numbers from backend - endpoint is under phoneNumberPricing
      const response = await api.get<any>({
        url: getApiUrl(endpoint.phoneNumberPricing.getUserPhoneNumbers),
        auth: true,
      });

      // API returns { statusCode, message, data: { items: [...], meta: {...} } }
      // The phone numbers are in response.data.items
      const phoneNumbersData = response?.data?.items || response?.data?.phoneNumbers || response?.data || [];
      const activeNumbers = Array.isArray(phoneNumbersData)
        ? phoneNumbersData.filter((pn: any) => pn.status === 'active')
        : [];
      setPhoneNumbers(activeNumbers);
      setShowPhoneNumbers(true);
    } catch (error) {
      console.error('Failed to load phone numbers:', error);
      // Show empty state instead of error
      setPhoneNumbers([]);
      setShowPhoneNumbers(true);
    } finally {
      setLoadingPhoneNumbers(false);
    }
  }

  async function handleSwapActiveNumber() {
    if (!selectedActivePhoneId || !swapAcknowledged) return;
    try {
      setSwappingActiveNumber(true);
      const { default: api } = await import('@/app/_module/services/api');
      const { getApiUrl } = await import('@/app/_module/services/config');
      const result: any = await api.patch<any>({
        url: getApiUrl('/agency/a2p/my-status/active-sms-number'),
        body: { phoneNumberId: selectedActivePhoneId },
        auth: true,
      });
      if (result?.statusCode === 200 || result?.data) {
        toast({
          title: 'Texting number changed',
          description: `Texts will send from ${result.data?.newPhoneNumber || 'the new number'} once the carriers activate it. That usually takes a few days and can take 2-3 weeks. Texts sent before then won't be delivered.`,
        });
        await loadPhoneNumbers(true);
        setSelectedActivePhoneId("");
      } else {
        toast({ title: "Couldn't change your number", description: result?.message || 'Try again.', variant: 'destructive' });
      }
    } catch (error: any) {
      toast({ title: "Couldn't change your number", description: error?.message || 'Try again.', variant: 'destructive' });
    } finally {
      setSwappingActiveNumber(false);
      setSwapConfirmOpen(false);
      setSwapAcknowledged(false);
    }
  }

  async function handleRelinkPhoneNumbers() {
    try {
      setRelinkingNumbers(true);
      const result = await a2pService.relinkPhoneNumbers();

      if (result.success) {
        toast({
          title: 'Numbers relinked',
          description: result.linkedCount > 0
            ? `Relinked ${result.linkedCount} number${result.linkedCount !== 1 ? 's' : ''} to your registration.`
            : 'Every number was already linked.',
        });
        // Reload phone numbers to show updated status
        await loadPhoneNumbers(true);
      } else {
        toast({
          title: "Couldn't relink numbers",
          description: result.message || 'Try again.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: "Couldn't relink numbers",
        description: error.message || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setRelinkingNumbers(false);
    }
  }

  const supportEmail = branding.supportEmail;
  const mailto = (subject: string, body?: string) =>
    `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}${body ? `&body=${encodeURIComponent(body)}` : ''}`;
  const supportLink = (
    <a href={mailto('Texting registration')} className={cn('font-medium text-neutral-900 underline underline-offset-4 hover:text-primary', focusRing)}>
      {supportEmail}
    </a>
  );
  const emailSupportButton = (subject: string, body?: string) => (
    <Button asChild size="lg" className={cn('px-6', focusRing)}>
      <a href={mailto(subject, body)}>
        <Mail />
        Email support
      </a>
    </Button>
  );

  // Native app: instruction only, same treatment as the area-code tabs.
  // After all hooks; before the loading branch so nothing A2P renders at all.
  if (isNativeApp) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <StatusPanel tone="neutral" label="Web only" title="Register on the web">
          <p>
            Texting registration isn&apos;t available in the app. Open {branding.brandName} in a browser on your
            computer to register or check your status.
          </p>
        </StatusPanel>
      </div>
    );
  }

  // Show wizard as full page when active
  if (showWizard) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
        <p className="mb-6 text-sm text-neutral-500">
          <span className="font-medium text-neutral-900">Texting registration</span> · A2P 10DLC
        </p>
        <A2PWizard
          resume={resumeWizard}
          onComplete={handleWizardComplete}
          onCancel={() => setShowWizard(false)}
        />
      </div>
    );
  }

  // Render based on status.
  // Brand vs campaign is TWO separate reviews. Brand can be approved (status='verified') while
  // the TCR campaign is still rejected/pending — SMS won't deliver until the campaign is also
  // approved. Downgrade currentStatus so the UI reflects reality instead of showing green
  // "Verification Complete" while the SMS widget is blocking sends.
  const rawStatus = status?.status || 'not_started';
  const anyStatus: any = status || {};
  const campaignStatusRaw = anyStatus.campaignStatus || anyStatus.campaign_status;
  let currentStatus: string = rawStatus;
  if (rawStatus === 'verified') {
    if (campaignStatusRaw === 'rejected') {
      currentStatus = 'rejected';
    } else if (campaignStatusRaw === 'pending' || campaignStatusRaw === 'in_progress') {
      currentStatus = 'pending';
    }
  }

  const brandType = status?.brandType;
  const isSoleProp = brandType === 'sole_proprietor';
  const typeLabel = brandType ? TYPE_LABEL[brandType] ?? null : null;
  const covered = !!anyStatus.coveredByAgency;
  const coveringName: string = anyStatus.coveringAgencyName || 'your agency';
  const registeredAs = status?.businessName || status?.campaignName || null;
  const submittedOn = formatDate(status?.submittedAt);
  const linkedCount = status?.linkedPhoneNumbers ?? 0;
  const totalCount: number | null = typeof anyStatus.totalPhoneNumbers === 'number' ? anyStatus.totalPhoneNumbers : null;

  const typeFact: Fact[] = typeLabel ? [{ label: 'Type', value: typeLabel }] : [];
  const submittedFact: Fact[] = submittedOn ? [{ label: 'Submitted', value: submittedOn }] : [];

  const supportIds = (
    <SupportIds
      ids={[
        { label: 'Registration ID', value: status?.verificationId },
        { label: 'Brand ID', value: status?.brandSid },
        { label: 'Campaign ID', value: status?.campaignSid },
        { label: 'Messaging ID', value: status?.messagingServiceSid },
      ]}
    />
  );
  const landingPage = status?.publicSlug ? <LandingPageSection slug={status.publicSlug} /> : null;

  const numbersSection = (description: React.ReactNode) => (
    <NumbersSection
      description={description}
      soleProp={isSoleProp}
      linkedCount={linkedCount}
      phoneNumbers={phoneNumbers}
      open={showPhoneNumbers}
      loading={loadingPhoneNumbers}
      relinking={relinkingNumbers}
      onToggle={() => (showPhoneNumbers ? setShowPhoneNumbers(false) : loadPhoneNumbers())}
      onRelink={handleRelinkPhoneNumbers}
    />
  );

  const progress = (steps: ReviewStep[]) => (
    <Section id="a2p-progress" title="Progress">
      <Panel className="px-4 py-5 sm:px-6">
        <ReviewSteps steps={steps} />
      </Panel>
    </Section>
  );

  let panel: React.ReactNode;
  let sections: React.ReactNode = null;

  if (loading) {
    panel = <LoadingPanel />;
  } else if (loadError) {
    panel = (
      <StatusPanel
        tone="neutral"
        label="Couldn't load"
        title="We couldn't load your registration"
        actions={
          <Button size="lg" onClick={loadStatus} className={cn('px-6', focusRing)}>
            <RotateCw />
            Try again
          </Button>
        }
      >
        <p>Check your connection and try again. If it keeps happening, email {supportLink}.</p>
      </StatusPanel>
    );
  } else if (currentStatus === 'not_started' || currentStatus === 'not_submitted') {
    const locked = eligibility?.blockReason === 'a2p_locked';
    const needsPaidNumber = eligibility?.blockReason === 'no_paid_number';
    const intro = (
      <p>
        Carriers filter business texts sent from unregistered numbers. Register once and your numbers can text your
        leads.
      </p>
    );
    const timeline: Fact[] = [
      fee?.applies
        ? { label: 'Cost', value: `$${((fee.amountCents ?? 2000) / 100).toFixed(2).replace(/\.00$/, '')}`, note: 'One time, paid after you submit' }
        : { label: 'Cost', value: 'Free', note: 'Your first registration' },
      { label: 'Our review', value: '1-2 business days' },
      { label: 'Carrier review', value: '3-5 days', note: 'Up to 2-3 weeks when busy' },
    ];

    if (isDemoMode()) {
      // A2P/10DLC registration is a real submission to The Campaign Registry with a real
      // carrier fee attached, so a sandbox visitor must never be able to fire it. Rather
      // than a dead button, this is the offer: someone who wants texting is the best
      // moment to ask for the trial. Opens in a new tab so the demo isn't thrown away.
      panel = (
        <StatusPanel
          tone="neutral"
          label="Not registered"
          title="Register your numbers to text leads"
          facts={timeline}
          actions={
            <Button
              size="lg"
              className={cn('px-6', focusRing)}
              onClick={() =>
                window.open(
                  process.env.NEXT_PUBLIC_DEMO_SIGNUP_URL || 'https://app.insuracentral.com/auth/sign-up',
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              Start a trial to register
              <ExternalLink />
            </Button>
          }
        >
          {intro}
        </StatusPanel>
      );
    } else if (locked) {
      panel = (
        <StatusPanel
          tone="blocked"
          label="Locked"
          title="Registration is locked on this account"
          facts={eligibility?.lockedAt ? [{ label: 'Locked on', value: formatDate(eligibility.lockedAt) }] : undefined}
          actions={emailSupportButton('Texting registration locked')}
        >
          <p>{eligibility?.blockMessage}</p>
        </StatusPanel>
      );
    } else if (isOnTrial) {
      panel = (
        <StatusPanel
          tone="neutral"
          label="Not registered"
          title="Register your numbers to text leads"
          facts={timeline}
          actions={
            <Button asChild size="lg" className={cn('px-6', focusRing)}>
              <Link href="/settings?tab=subscription">Upgrade to register</Link>
            </Button>
          }
        >
          {intro}
          <p>
            <strong>Registration opens on a paid plan.</strong> Upgrade to start it now.
          </p>
        </StatusPanel>
      );
    } else if (needsPaidNumber) {
      const trialCount = eligibility?.trialNumberCount ?? 0;
      panel = (
        <StatusPanel
          tone="waiting"
          label="Action needed"
          title="Buy a phone number to register"
          facts={timeline}
          actions={
            <Button asChild size="lg" className={cn('px-6', focusRing)}>
              <Link href="/settings/phone-numbers">Buy a phone number</Link>
            </Button>
          }
        >
          <p>Registration needs a paid number. Free trial numbers expire during review and break the registration.</p>
          {trialCount > 0 && (
            <p>
              You have {trialCount} free trial number{trialCount === 1 ? '' : 's'}. Convert one to paid or buy a new
              one.
            </p>
          )}
        </StatusPanel>
      );
    } else {
      const checking = !eligibility && !eligibilityError;
      panel = (
        <StatusPanel
          tone="neutral"
          label="Not registered"
          title="Register your numbers to text leads"
          facts={timeline}
          actions={
            eligibilityError ? (
              <Button size="lg" onClick={loadEligibility} className={cn('px-6', focusRing)}>
                <RotateCw />
                Try again
              </Button>
            ) : (
              <>
                <Button
                  size="lg"
                  onClick={handleStartVerification}
                  disabled={!eligibility?.canSubmit}
                  className={cn('px-6', focusRing)}
                >
                  {checking ? 'Checking your account…' : draft ? 'Resume registration' : 'Start registration'}
                </Button>
                {draft && eligibility?.canSubmit && (
                  <Button variant="ghost" onClick={() => openWizard(false)} className={cn('h-11', focusRing)}>
                    Start over
                  </Button>
                )}
              </>
            )
          }
        >
          {intro}
          {draft && (
            <p>
              You saved your progress on {formatDate(draft.savedAt)}: <strong>{draftProgress(draft)}</strong>.
            </p>
          )}
          {eligibilityError && <p>We couldn&apos;t check whether your account can register.</p>}
        </StatusPanel>
      );
    }

    if (!locked) {
      sections = (
        <RegistrationGuide
          canRegisterForAgency={canRegisterForAgency}
          agencyName={currentAgencyCtx?.agency?.name}
        />
      );
    }
  } else if (currentStatus === 'pending_review') {
    const feeShown = !!fee?.applies && !!fee.verificationId;
    const feePaid = fee?.status === 'paid';
    const feeProcessing = fee?.status === 'processing';
    const feeDue = !!fee?.due;
    const amount = `$${((fee?.amountCents ?? 2000) / 100).toFixed(2).replace(/\.00$/, '')}`;
    const feeFact: Fact[] = feeShown
      ? [{ label: 'Registration fee', value: feePaid ? 'Paid' : feeProcessing ? 'Processing' : `${amount} due` }]
      : [];

    panel = feeDue ? (
      <StatusPanel
        tone="waiting"
        label="Payment needed"
        title={`Pay the ${amount} registration fee`}
        facts={[...typeFact, ...submittedFact, ...feeFact]}
        actions={
          <>
            <Button size="lg" onClick={() => handlePayFee()} disabled={payingFee} className={cn('px-6', focusRing)}>
              {payingFee && <Loader2 className="animate-spin" />}
              {payingFee ? 'Processing…' : fee?.card ? `Pay ${amount} with ${cardLabel(fee.card)}` : `Pay ${amount}`}
            </Button>
            {fee?.card && (
              <Button variant="ghost" onClick={() => handlePayFee('checkout')} disabled={payingFee} className={cn('h-11', focusRing)}>
                Pay with a different card
              </Button>
            )}
          </>
        }
      >
        <p>Each texting registration has a one-time {amount} fee. Our team reviews yours once it&apos;s paid.</p>
        <p>
          {fee?.card
            ? "We'll charge your card on file. If it doesn't go through, you'll finish on a secure checkout page."
            : "You'll pay on a secure checkout page."}
        </p>
      </StatusPanel>
    ) : (
      <StatusPanel
        tone="waiting"
        label="In review"
        title="In review with our team"
        facts={[...typeFact, ...submittedFact, ...feeFact]}
      >
        <p>
          {feeProcessing
            ? "Your registration fee is processing with your bank. Our review starts once it's confirmed."
            : `We're checking your details${isSoleProp ? '' : ' and CP 575 letter'}. That usually takes 1-2 business days, and we'll notify you once it's approved.`}
        </p>
      </StatusPanel>
    );
    sections = (
      <>
        {progress([
          { key: 'submitted', title: 'Submitted', detail: submittedOn, state: 'done' },
          ...(feeShown
            ? [{
                key: 'fee',
                title: 'Registration fee',
                detail: feePaid
                  ? `Paid${fee?.paidAt ? ` ${formatDate(fee.paidAt)}` : ''}.`
                  : feeProcessing
                    ? 'Processing with your bank.'
                    : `${amount}, one time.`,
                state: feePaid ? ('done' as const) : feeDue ? ('action' as const) : ('current' as const),
              }]
            : []),
          {
            key: 'ours',
            title: 'Our review',
            detail: `Checking your details${isSoleProp ? '' : ' and CP 575 letter'}. Usually 1-2 business days.`,
            state: feeDue || feeProcessing ? 'upcoming' : 'current',
          },
          ...(isSoleProp
            ? [{ key: 'identity', title: "Confirm it's you", detail: "You'll confirm from your mobile phone.", state: 'upcoming' as const }]
            : []),
          { key: 'carriers', title: 'Carrier review', detail: 'Usually 3-5 days, up to 2-3 weeks when busy.', state: 'upcoming' },
          { key: 'ready', title: 'Ready to text', detail: 'Your numbers are linked automatically.', state: 'upcoming' },
        ])}
        {landingPage}
        <p className="text-sm text-neutral-500">Taking more than a month? Email {supportLink}.</p>
      </>
    );
  } else if (currentStatus === 'pending_payment') {
    panel = (
      <StatusPanel
        tone="waiting"
        label="Payment needed"
        title="Finish checkout to submit"
        facts={[...typeFact]}
        actions={
          <Button size="lg" onClick={handleStartVerification} className={cn('px-6', focusRing)}>
            Finish checkout
          </Button>
        }
      >
        <p>Your registration is saved. It goes to review as soon as checkout is complete.</p>
      </StatusPanel>
    );
  } else if (currentStatus === 'pending') {
    const identityDone = status?.brandStatus === 'approved';
    const telnyxPin = isSoleProp && anyStatus.provider === 'telnyx' && !!status?.otpPending;
    const replyYes = isSoleProp && !identityDone && anyStatus.provider !== 'telnyx';
    const brandOnly = rawStatus === 'verified';

    if (telnyxPin) {
      panel = (
        <StatusPanel
          tone="waiting"
          label="Your turn"
          title="Enter the code texted to your business phone"
          facts={[...typeFact, ...submittedFact]}
          detail={<OtpForm onVerified={loadStatus} />}
        >
          <p>It confirms you own the number. Carrier review starts as soon as the code is accepted.</p>
        </StatusPanel>
      );
    } else if (replyYes) {
      panel = (
        <StatusPanel
          tone="waiting"
          label="Your turn"
          title="Reply YES to finish"
          facts={[...typeFact, ...submittedFact]}
        >
          <p>
            Look for a text on your mobile phone about <strong>US A2P Messaging</strong> registration. It can take a
            few minutes to arrive. <strong>Reply YES within 24 hours</strong> to confirm it&apos;s you, and carrier
            review starts right after.
          </p>
        </StatusPanel>
      );
    } else {
      panel = (
        <StatusPanel
          tone="waiting"
          label="In review"
          title="In review with the carriers"
          facts={[...typeFact, ...submittedFact]}
        >
          <p>
            {brandOnly ? 'Your business is approved. ' : 'Our team approved your registration. '}
            The carriers usually finish in under a week, sometimes 2-3 weeks when they&apos;re busy. Your numbers are
            linked automatically once they approve.
          </p>
        </StatusPanel>
      );
    }

    const identityWaiting = isSoleProp && !identityDone;
    sections = (
      <>
        {progress([
          { key: 'submitted', title: 'Submitted', detail: submittedOn, state: 'done' },
          { key: 'ours', title: 'Our review', detail: 'Approved by our team.', state: 'done' },
          ...(isSoleProp
            ? [{
                key: 'identity',
                title: "Confirm it's you",
                detail: identityDone
                  ? 'Confirmed from your phone.'
                  : telnyxPin
                    ? 'Enter the code texted to your business phone.'
                    : 'Reply YES to the text on your mobile phone.',
                state: identityDone ? ('done' as const) : ('action' as const),
              }]
            : []),
          {
            key: 'carriers',
            title: 'Carrier review',
            detail: 'Usually under a week, up to 2-3 weeks when busy.',
            state: identityWaiting ? 'upcoming' : 'current',
          },
          { key: 'ready', title: 'Ready to text', detail: 'Your numbers are linked automatically.', state: 'upcoming' },
        ])}
        {landingPage}
        {supportIds}
      </>
    );
  } else if (currentStatus === 'verified') {
    const messages: number | null = typeof status?.messagesThisMonth === 'number' ? status.messagesThisMonth : null;
    const numbersFact: Fact = {
      label: 'Numbers texting',
      value: totalCount !== null && totalCount > linkedCount ? `${linkedCount} of ${totalCount}` : `${linkedCount}`,
      note: isSoleProp ? 'One per sole proprietor registration' : undefined,
    };
    // The backend defaults health to "Excellent" when nothing was sent, so it only means something once texts go out.
    const healthFact: Fact[] = messages && status?.campaignHealth ? [{ label: 'Delivery this month', value: status.campaignHealth }] : [];
    const agencyCampaignFact: Fact[] =
      typeof anyStatus.campaignNumbersUsed === 'number' && typeof anyStatus.campaignNumberLimit === 'number'
        ? [{ label: 'Agency campaign', value: `${anyStatus.campaignNumbersUsed} of ${anyStatus.campaignNumberLimit} numbers` }]
        : [];

    panel = covered ? (
      <StatusPanel
        tone="ok"
        label="Active"
        title="You can text"
        facts={[{ label: 'Covered by', value: coveringName, wide: true }, numbersFact]}
      >
        <p>
          You&apos;re covered by <strong>{coveringName}</strong>&apos;s registration, so you don&apos;t need your own.
          Numbers you buy are added automatically.
        </p>
      </StatusPanel>
    ) : (
      <StatusPanel
        tone="ok"
        label="Active"
        title="You can text"
        facts={[
          ...(registeredAs ? [{ label: 'Registered as', value: registeredAs, wide: true }] : []),
          ...typeFact,
          ...(status?.approvedAt ? [{ label: 'Approved', value: formatDate(status.approvedAt) }] : []),
          numbersFact,
          ...(messages !== null ? [{ label: 'Texts this month', value: messages.toLocaleString() }] : []),
          ...healthFact,
          ...agencyCampaignFact,
        ]}
      >
        <p>
          {isSoleProp
            ? 'Your registration is approved for one texting number, shown below.'
            : 'Your registration is approved. Every number on your account can text, and numbers you buy are added automatically.'}
        </p>
      </StatusPanel>
    );

    sections = (
      <>
        {isSoleProp && !covered && (
          <TextingNumberSection
            phoneNumbers={phoneNumbers}
            loading={loadingPhoneNumbers}
            selectedId={selectedActivePhoneId}
            onSelect={setSelectedActivePhoneId}
            swapping={swappingActiveNumber}
            confirmOpen={swapConfirmOpen}
            onConfirmOpenChange={setSwapConfirmOpen}
            acknowledged={swapAcknowledged}
            onAcknowledge={setSwapAcknowledged}
            onRequestChange={() => {
              setSwapAcknowledged(false);
              setSwapConfirmOpen(true);
            }}
            onConfirm={handleSwapActiveNumber}
          />
        )}
        {numbersSection(
          covered
            ? `Linked to ${coveringName}'s registration. Numbers you buy are added automatically.`
            : isSoleProp
              ? 'Every number on your account, and which one sends texts.'
              : 'Every number on your account is linked to your registration.',
        )}
        {/* Landline caller ID for business registrations — hides itself for sole props. */}
        <CallerIdNameCard />
        {!covered && landingPage}
        {canRegisterForAgency && !covered && (
          // Register on behalf of the agency. Shown to owners/admins regardless of
          // their own registration — covering the downline is a separate act.
          <Section
            id="a2p-register-agency"
            title={`Register ${currentAgencyCtx?.agency?.name || 'your agency'}`}
            description="One registration covers every agent under you and the numbers they buy, so nobody registers one by one. Your own registration stays as it is."
          >
            <Panel className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <p className="text-sm leading-relaxed text-neutral-600">
                You&apos;ll need the agency&apos;s EIN (at least 45 days old) and its IRS CP 575 letter.
              </p>
              <Button variant="outline" onClick={() => openWizard(!!draft)} className={cn('h-11 shrink-0 sm:h-10', focusRing)}>
                {draft ? 'Resume registration' : 'Register for my agency'}
              </Button>
            </Panel>
          </Section>
        )}
        {isSoleProp && !covered && (
          <Section
            id="a2p-upgrade"
            title="Need more than one texting number?"
            description="Low Volume Standard lets every number on your account text. It needs an EIN at least 45 days old and a new registration, which support sets up with you."
          >
            <Panel className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <p className="min-w-0 break-all text-sm text-neutral-600">{supportEmail}</p>
              <Button asChild variant="outline" className={cn('h-11 shrink-0 sm:h-10', focusRing)}>
                <a
                  href={mailto(
                    'A2P upgrade request: Sole Proprietor to Low Volume Standard',
                    'Hi Support,\n\nI would like to upgrade my A2P registration from Sole Proprietor to Low Volume Standard.\n\nMy EIN: [Please include your EIN]\nEIN issue date: [Please include the date your EIN was issued]\n\nThank you!',
                  )}
                >
                  <Mail />
                  Email support to upgrade
                </a>
              </Button>
            </Panel>
          </Section>
        )}
        {!covered && supportIds}
      </>
    );
  } else if (currentStatus === 'rejected') {
    const campaignOnly = campaignStatusRaw === 'rejected' && rawStatus === 'verified';
    const rejectedOn = formatDate(status?.rejectedAt);
    panel = (
      <StatusPanel
        tone="blocked"
        label="Rejected"
        title={campaignOnly ? 'The carriers rejected your texting campaign' : 'Your registration was rejected'}
        facts={[...(rejectedOn ? [{ label: 'Rejected on', value: rejectedOn }] : []), ...typeFact]}
        detail={
          <div className="rounded-lg bg-neutral-50 px-4 py-3.5 ring-1 ring-inset ring-neutral-200/70">
            <p className="text-xs text-neutral-500">Reason given</p>
            <p className="mt-1 text-pretty text-sm leading-relaxed text-neutral-900">
              {status?.rejectionReason ||
                (campaignOnly
                  ? 'No reason was given. The usual causes are the sample messages, the opt-in description, or the business page.'
                  : 'No reason was given. Support can look it up for you.')}
            </p>
          </div>
        }
        actions={emailSupportButton('Texting registration rejected')}
      >
        <p>
          {campaignOnly
            ? "Your business is approved, but carrier review rejected the campaign, so texts won't deliver until it's fixed and resubmitted. Email support and we'll fix it with you."
            : "Email support and we'll go over the reason and what to change."}
        </p>
      </StatusPanel>
    );
    sections = supportIds;
  } else if (currentStatus === 'expired') {
    panel = (
      <StatusPanel
        tone="neutral"
        label="Expired"
        title="Your texting registration expired"
        facts={[...typeFact]}
        actions={emailSupportButton('Reactivate texting registration')}
      >
        <p>It expired from inactivity. Your numbers still make and receive calls. Email support to turn texting back on.</p>
      </StatusPanel>
    );
    sections = supportIds;
  } else {
    panel = (
      <StatusPanel
        tone="neutral"
        label="Unknown"
        title="We couldn't read your registration status"
        actions={emailSupportButton('Texting registration status')}
      >
        <p>Email support and we&apos;ll check it for you.</p>
      </StatusPanel>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
      <p className="text-sm text-neutral-500">
        <span className="font-medium text-neutral-900">Texting registration</span> · A2P 10DLC
      </p>
      <div className="mt-4">{panel}</div>
      {sections && <div className="mt-10 space-y-10">{sections}</div>}
    </div>
  );
}
